import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import { LoadingComponent, useExecutionDataView } from "@gooddata/sdk-ui";
import { newAbsoluteDateFilter, newAttributeSort, newMeasureSort, newPositiveAttributeFilter } from "@gooddata/sdk-model";
import moment from "moment";
import "./print.css";
import * as Md from "../../../md/full";
import { useBasketContext } from "../../../contexts/BasketProvider";
import UserContext from "../../../contexts/User";
import FiltersContext from "../../../contexts/Filters";
import MeasuresContext from "../../../contexts/Measures";
import { BasketBarChart } from "../BasketBarChart";
import { BasketLineChart } from "../BasketLineChart";
import { useAppContext } from "../../../contexts/AppContext";
import { BasketReference } from "../BasketReference";
import UpDownButton from "../../Buttons/UpDownButton";
import { newJsonPivot } from "../../../utils/jsonPivot";
import { colors } from "../../../constants";
import { Selectedcust, DndButton, DnDModal } from "../../Layout/Selectedcust";
import BasketSynthesisChart from "./BasketSynthesisChart";
import { message, Tooltip } from "antd";
import BasketVersionMatch from "./BasketVersionMatch";
import { CarIcon } from "../../Icons/CarIcon";
import { FaTrash } from "react-icons/fa";
import { MonthlyConfiguration, WeeklyConfiguration } from "./AggregatedBasketViz";
import { getCurrencySymbolByCountryName } from "../../../utils/currencyUtils";

const MissingVehicle = ({ vehicle, getVehicleAvailability, onSelect }) => {
    const availableCustomers = getVehicleAvailability(vehicle.value);
    const tooltipContent =
        availableCustomers.length > 0 ? (
            <>
                <div>
                    <span className="font-bold text-xs text-white">Vehicle available for : </span>
                    <span className="text-xs text-white">{availableCustomers.join(", ")}</span>
                </div>
                <span className="text-xs text-white">Please adjust your players slots or click to find a match for your current players selection </span>
            </>
        ) : (
            <span className="text-xs text-white">Vehicle not available for this month! click to find a match for your current players selection</span>
        );

    const handleClick = useCallback(() => {
        if (vehicle.make && vehicle.model && vehicle.energy) {
            onSelect(vehicle);
        } else {
            message.error({
                content: "Operation could not be implemented on this vehicle !",
                style: {
                    top: "80px",
                    right: "20px",
                    position: "absolute",
                },
                duration: 2,
            });
        }
    }, [vehicle, onSelect]);

    return (
        <Tooltip title={tooltipContent}>
            <div className="flex items-center border border-gray-400 rounded-md px-2 py-1 col-span-1 text-gray-400 cursor-pointer hover:bg-indigo-700 hover:border-indigo-500 hover:text-white group relative" onClick={handleClick}>
                <CarIcon className="w-4 h-4 text-secondary-300 mr-1" />
                <span className="font-bold text-[10px]">{vehicle.value}</span>
            </div>
        </Tooltip>
    );
};

const PdfHeader = ({ basketName, country, date, channel1, duration, kpi, adjdeposititle }) => (
    <div className="flex justify-between items-end pb-2 border-b border-indigo-700">
        <div className="flex-1 flex flex-col w-full items-stretch">
            <span className="leading-5 text-xl font-bold text-indigo-700">
                {country} - Basket {basketName} - {date}
            </span>
            <span className="font-semibold">
                Monthly {kpi} in {getCurrencySymbolByCountryName(country)} {adjdeposititle ? "| " + adjdeposititle : ""} | {channel1} Channel | {duration} (Months/{country === "United Kingdom" ? "Miles" : "KM"})
            </span>
        </div>
        <img className="relative float-right z-10" alt="Experteye_Logo" src="images/experteye-logo.png" height={12.0} />
    </div>
);

function BasketViz({ basket, printMode, custIds = [], selectedKpi, filterCountryId, filterMthCode, filterChannel1, filterDuration, filterExtrapType, filterRolledForward, printStatus, setIsLoading, adjdeposititle }) {
    const { displayMode, filterPeriodFrequency } = useContext(FiltersContext);
    const userPreferenceContext = useContext(UserContext);
    const { user, defaultUserPref } = useContext(UserContext);
    const { seriesBy } = useContext(MeasuresContext);

    const versionSlots = useMemo(() => basket?.basketFilters?.filter((elem) => elem.type === "version_std").map((elem) => elem.value) || [], [basket]);
    const versionStdFilter = useMemo(() => (versionSlots.length > 0 ? newPositiveAttributeFilter(Md.VersionStd, versionSlots) : null), [versionSlots]);

    const [colorSeries, setColorSeries] = useState({});
    const [colorVSeries, setColorVSeries] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const { getCountryByCode, getColorByCustId, getCustlabelByCustId, showListPrices: showListPricesContext, dateRange } = useAppContext();
    const { updateBasketPrintStatus, editBasket } = useBasketContext();
    const [showVersionMatch, setShowVersionMatch] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState();
    const ref = useRef(null);
    const [showGraph, setShowGraph] = useState(user?.basket_sections?.Basket_Overview);
    // Data used to render trend charts
    const [basketReference, setBasketReference] = useState({
        customer: undefined,
        versionStd: undefined,
        value: undefined,
    });

    const [readyVizCounter, setReadyVizCounter] = useState(0);
    const [showBarFigures, setShowBarFigures] = useState(userPreferenceContext?.user?.basket_sections?.Basket_BarChart);
    const [showTrendCharts, setShowTrendCharts] = useState(userPreferenceContext?.user?.basket_sections?.Basket_Trends);

    const [overviewDivWidth, setOverviewDivWidth] = useState(ref?.current?.getBoundingClientRect().width);
    // const incrementReadyVizCounter = () => {
    //     setReadyVizCounter(readyVizCounter + 1);
    // };
    const custSlots = useMemo(
        () =>
            Object.entries(userPreferenceContext.defaultUserPref)
                .filter(([key, val]) => key.includes("customer_slot") && val !== null)
                .map(([key, val]) => val),
        [userPreferenceContext.defaultUserPref],
    );
    const custIdFilters = useMemo(() => (custSlots.length > 0 ? newPositiveAttributeFilter(Md.CustId, custSlots) : null), [custSlots]);

    const formattedDateRange = dateRange.map((dt) => dt.format("YYYY-MM-DD"));
    const basketTrendFilters = useMemo(
        () =>
            [
                versionStdFilter,
                filterChannel1,
                filterCountryId,
                filterDuration,
                filterExtrapType,
                filterRolledForward,
                custIdFilters,
                formattedDateRange.length === 2 ? newAbsoluteDateFilter(Md.DateDatasets.DateOfScraping.ref, formattedDateRange[0], formattedDateRange[1]) : null,
                !Boolean(defaultUserPref?.fl_weekly) ? newPositiveAttributeFilter(Md.Latestmchar, ["1"]) : null,
            ].filter(Boolean),
        [versionStdFilter, filterChannel1, filterCountryId, filterDuration, filterExtrapType, filterRolledForward, custIdFilters, formattedDateRange, defaultUserPref],
    );

    const basketSpotFilters = useMemo(
        () => [versionStdFilter, filterChannel1, filterCountryId, filterDuration, custIdFilters, filterMthCode, filterPeriodFrequency, filterExtrapType, filterRolledForward].filter(Boolean),
        [versionStdFilter, filterChannel1, filterCountryId, filterDuration, custIdFilters, filterMthCode, filterPeriodFrequency, filterExtrapType, filterRolledForward],
    );

    const frequencyConfiguration = useMemo(() => (defaultUserPref?.fl_weekly ? WeeklyConfiguration : MonthlyConfiguration), [defaultUserPref]);

    const skipExecution =
        !versionStdFilter || !custIdFilters || !filterCountryId?.positiveAttributeFilter?.in?.values?.length || !filterMthCode?.positiveAttributeFilter?.in?.values?.length || !filterChannel1?.positiveAttributeFilter?.in?.values?.length || !filterDuration?.positiveAttributeFilter?.in?.values?.length;

    const { result, status } = useExecutionDataView(
        {
            execution: skipExecution
                ? undefined
                : {
                      seriesBy: seriesBy,
                      slicesBy: [Md.CustId, Md.VersionStd, Md.Rolledforward],
                      filters: basketSpotFilters,
                      sortBy: [newMeasureSort(seriesBy[selectedKpi.index]), newAttributeSort(Md.CustId)],
                  },
        },
        [showListPricesContext],
    );
    // get all Versions with their CustId
    const { result: resultAllVehicles } = useExecutionDataView({
        execution: skipExecution
            ? undefined
            : {
                  seriesBy: seriesBy,
                  slicesBy: [Md.CustId, Md.VersionStd],
                  filters: [versionStdFilter, filterChannel1, filterCountryId, filterMthCode, filterDuration, filterPeriodFrequency, filterExtrapType, filterRolledForward].filter(Boolean),
              },
    });

    // Reference line
    const { result: referenceResult, status: referenceStatus } = useExecutionDataView({
        execution:
            !basketReference?.customer || !basketReference?.versionStd || skipExecution
                ? undefined
                : {
                      seriesBy: seriesBy,
                      slicesBy: [Md.VersionStd, Md.CustId],
                      filters: [
                          newPositiveAttributeFilter(Md.CustId, [basketReference?.customer]),
                          newPositiveAttributeFilter(Md.VersionStd, [basketReference?.versionStd]),
                          versionStdFilter,
                          filterChannel1,
                          filterCountryId,
                          filterDuration,
                          custIdFilters,
                          filterMthCode,
                          filterExtrapType,
                          filterRolledForward,
                      ].filter(Boolean),
                  },
    });

    const { result: trendResult, status: trendStatus } = useExecutionDataView({
        execution: skipExecution
            ? undefined
            : {
                  seriesBy: seriesBy,
                  slicesBy: [Md.CustId, frequencyConfiguration.field, Md.VersionStd, Md.Rolledforward],
                  filters: basketTrendFilters,
              },
    });

    const spotData = useMemo(() => {
        if (!result) return;
        const data = result.data().series().firstForMeasure(seriesBy[selectedKpi.index]);
        return data?.dataPoints().map((dp) =>
            Object.freeze({
                value: Number(dp.rawValue) === 0 ? null : selectedKpi.index === 24 ? Number((Number(dp.rawValue) * 100).toFixed(2)) : Math.round(Number(dp.rawValue)),
                custId: dp.sliceDesc.sliceTitles()[0],
                versionStd: dp.sliceDesc.sliceTitles()[1],
                rolledforward: dp.sliceDesc.sliceTitles()[2],
            }),
        );
    }, [result, selectedKpi, seriesBy]);

    const rolledForwardDataPoints = useMemo(() => {
        if (!spotData) return new Set();
        return new Set(spotData.filter((dp) => dp.rolledforward === "Yes").map((dp) => `${dp.versionStd}|${dp.custId}`));
    }, [spotData]);

    const listPrices = useMemo(() => {
        if (!result) return [];
        const lpdata = result
            ?.data()
            .series()
            .firstForMeasure(seriesBy[1])
            ?.dataPoints()
            .map((dp) =>
                Object.freeze({
                    value: Math.round(Number(dp.rawValue)),
                    custId: dp.sliceDesc.sliceTitles()[0],
                    versionStd: dp.sliceDesc.sliceTitles()[1],
                }),
            );
        if (lpdata) {
            return newJsonPivot(lpdata, { row: "versionStd", column: "custId", value: "value" });
        }
        return [];
    }, [result, seriesBy]);

    const monthVData = useMemo(() => {
        if (!spotData) return [];
        return newJsonPivot(spotData, { row: "versionStd", column: "custId", value: "value" });
    }, [spotData]);

    const monthData = useMemo(() => {
        if (!spotData) return [];
        return newJsonPivot(spotData, { row: "custId", column: "versionStd", value: "value" });
    }, [spotData]);

    const scale = useMemo(() => {
        const maxValue = monthVData.reduce((acc, nextElem) => {
            const elemMaxValue = Object.entries(nextElem)
                .filter(([key, val]) => custSlots.includes(key))
                .reduce((maxValue, [key, nextValue]) => {
                    return maxValue > nextValue ? maxValue : nextValue;
                }, 0);
            return acc > elemMaxValue ? acc : elemMaxValue;
        }, 0);
        return { maxValue };
    }, [monthVData, custSlots]);

    const trendSlices = useMemo(() => trendResult?.data().slices().toArray(), [trendResult]);

    const scrapingDates = useMemo(() => {
        if (!trendSlices) return [];
        const dates = trendSlices.reduce((acc, slice) => {
            const scrapingDate = frequencyConfiguration.formatSliceField(slice.sliceTitles()[1]);
            !acc.includes(scrapingDate) && acc.push(scrapingDate);
            return acc;
        }, []);
        dates.sort();
        return dates;
    }, [trendSlices, frequencyConfiguration]);

    const rolledForwardTrendPoints = useMemo(() => {
        if (!trendSlices) return new Set();
        return new Set(
            trendSlices
                .filter((slice) => slice.sliceTitles()[3] === "Yes")
                .map((slice) => {
                    const sliceTitles = slice.sliceTitles();
                    const custId = sliceTitles[0];
                    const versionStd = sliceTitles[2];
                    const date = frequencyConfiguration.formatSliceField(sliceTitles[1]);
                    return `${custId}|${versionStd}|${date}`;
                }),
        );
    }, [trendSlices, frequencyConfiguration]);

    const serieColors = useMemo(() => {
        if (!trendSlices) return {};
        let trendColorCopy = colors.slice();
        return trendSlices.reduce((acc, slice) => {
            let attrValue = displayMode.attributes.reduce((acc, attr) => acc + " " + slice.sliceTitles()[2], "");
            if (!(attrValue in acc)) {
                acc[attrValue] = trendColorCopy.find((item) => !Object.values(acc).includes(item));
            }
            return acc;
        }, {});
    }, [trendSlices, displayMode]);

    const serieVColors = useMemo(() => {
        if (!trendSlices) return {};
        return trendSlices.reduce((acc, slice) => {
            const custId = slice.sliceTitles()[0];
            let attrValue = displayMode.attributes.reduce((acc, attr) => acc + "" + custId, "");
            if (!(attrValue in acc)) {
                acc[attrValue] = getColorByCustId(custId);
            }
            return acc;
        }, {});
    }, [trendSlices, displayMode, getColorByCustId]);

    const trendData = useMemo(() => {
        const dataSerie = trendResult?.data().series().firstForMeasure(seriesBy[selectedKpi.index]);
        if (!dataSerie || !scrapingDates.length) return {};

        return dataSerie.dataPoints().reduce((acc, dp) => {
            const sliceTitles = dp.sliceDesc.sliceTitles();
            const custValue = sliceTitles[0];
            const serieTitle = displayMode.attributes.reduce((acc, attr, index) => acc + " " + sliceTitles[index + 2], "");
            const scrapeDate = frequencyConfiguration.formatSliceField(sliceTitles[1]);
            const dataPoint = Number(dp.rawValue) === 0 ? null : selectedKpi.index === 24 ? Number((Number(dp.rawValue) * 100).toFixed(2)) : Math.round(Number(dp.rawValue));

            if (custValue in acc) {
                let serieElem = acc[custValue].find((item) => item.name === serieTitle);
                if (serieElem) {
                    const mthIndex = scrapingDates.findIndex((item) => item === scrapeDate);
                    serieElem.data[mthIndex] = [scrapeDate, dataPoint];
                } else {
                    let dataValues = Array(scrapingDates?.length)
                        .fill(null)
                        .map((_, i) => [scrapingDates[i], null]);
                    const mthIndex = scrapingDates.findIndex((item) => item === scrapeDate);
                    dataValues[mthIndex] = [scrapeDate, dataPoint];
                    acc[custValue].push({
                        name: serieTitle,
                        data: dataValues,
                        color: serieColors[serieTitle],
                    });
                }
            } else {
                let dataValues = Array(scrapingDates?.length)
                    .fill(null)
                    .map((_, i) => [scrapingDates[i], null]);
                const mthIndex = scrapingDates.findIndex((item) => item === scrapeDate);
                dataValues[mthIndex] = [scrapeDate, dataPoint];
                acc[custValue] = [
                    {
                        name: serieTitle,
                        data: dataValues,
                        color: serieColors[serieTitle],
                    },
                ];
            }
            return acc;
        }, {});
    }, [trendResult, seriesBy, selectedKpi, scrapingDates, displayMode, frequencyConfiguration, serieColors]);

    const trendDataV = useMemo(() => {
        const dataSerie = trendResult?.data().series().firstForMeasure(seriesBy[selectedKpi.index]);
        if (!dataSerie || !scrapingDates.length) return {};

        return dataSerie.dataPoints().reduce((acc, dp) => {
            const sliceTitles = dp.sliceDesc.sliceTitles();
            const versionStd = sliceTitles[2];
            const custId = sliceTitles[0];
            const custSerieTitle = getCustlabelByCustId(custId);
            const scrapingDate = frequencyConfiguration.formatSliceField(sliceTitles[1]);
            const dataPoint = Number(dp.rawValue) === 0 ? null : selectedKpi.index === 24 ? Number((Number(dp.rawValue) * 100).toFixed(2)) : Math.round(Number(dp.rawValue));

            if (versionStd in acc) {
                let serieElem = acc[versionStd].find((item) => item.name === custSerieTitle);
                if (serieElem) {
                    const mthIndex = scrapingDates.findIndex((item) => item === scrapingDate);
                    serieElem.data[mthIndex] = [scrapingDate, dataPoint];
                } else {
                    let dataValues = Array(scrapingDates?.length)
                        .fill(null)
                        .map((_, i) => [scrapingDates[i], null]);
                    const mthIndex = scrapingDates.findIndex((item) => item === scrapingDate);
                    dataValues[mthIndex] = [scrapingDate, dataPoint];
                    acc[versionStd].push({
                        name: custSerieTitle,
                        data: dataValues,
                        color: getColorByCustId(custId),
                    });
                }
            } else {
                let dataValues = Array(scrapingDates?.length)
                    .fill(null)
                    .map((_, i) => [scrapingDates[i], null]);
                const mthIndex = scrapingDates.findIndex((item) => item === scrapingDate);
                dataValues[mthIndex] = [scrapingDate, dataPoint];
                acc[versionStd] = [
                    {
                        name: custSerieTitle,
                        data: dataValues,
                        color: getColorByCustId(custId),
                    },
                ];
            }
            return acc;
        }, {});
    }, [trendResult, seriesBy, selectedKpi, scrapingDates, frequencyConfiguration, getCustlabelByCustId, getColorByCustId]);

    // const measures = [24, 23].includes(selectedKpi.index) ? seriesBy : [seriesBy[selectedKpi.index]];
    var monthDate = moment(filterMthCode.positiveAttributeFilter.in?.values[0], "YYYY MM").format("MMMM YY");
    const country = filterCountryId.positiveAttributeFilter.in?.values[0];
    const countryLabel = getCountryByCode(country);

    const resizeWindow = useCallback(() => {
        if (ref?.current) setOverviewDivWidth(ref?.current?.getBoundingClientRect().width);
    }, [ref]);

    useEffect(() => {
        setReadyVizCounter(0);
    }, []);

    useEffect(() => {
        // To fix responsiveness of the chart when resizing the browser
        window.addEventListener("resize", resizeWindow);
        return () => {
            window.removeEventListener("resize", resizeWindow);
        };
    }, [resizeWindow]);

    useEffect(() => {
        if (custIds?.length > 0 && readyVizCounter === custIds?.length * 2) {
            updateBasketPrintStatus(basket);
        }
    }, [readyVizCounter, custIds, basket, updateBasketPrintStatus]);

    useEffect(() => {
        if (status !== "loading" && referenceStatus !== "loading" && trendStatus !== "loading") {
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }
    }, [status, referenceStatus, trendStatus, setIsLoading]);

    useEffect(() => {
        // if the basket ref never changed, then attached to the reference_line from the db or the default basket filter
        if (basket !== undefined && Object.values(basketReference).filter((val) => val !== undefined).length === 0) {
            const referenceLine = basket.reference_line || basketReference;
            if (referenceLine instanceof Object) {
                setBasketReference({
                    ...referenceLine,
                });
            } else {
                let defaultReference = basket.basketFilters.find((elem) => elem.reference === 1);
                setBasketReference((prev) => ({
                    ...prev,
                    versionStd: defaultReference?.value,
                }));
            }
        }
    }, [basket]);

    useEffect(() => {
        if (spotData) {
            // setRolledForwardDataPoints(rolledForwardDataPoints); // This is now calculated by useMemo
            // setListPrices(listPrices); // This is now calculated by useMemo
            // setScale(scale); // This is now calculated by useMemo
            // setMthData(monthData); // This is now calculated by useMemo
            // setMthVData(monthVData); // This is now calculated by useMemo
        }
        return () => {
            // setMthData([]);
            // setMthVData([]);
        };
    }, [spotData]);

    useEffect(() => {
        if (trendResult) {
            // setRolledForwardTrendPoints(rolledForwardTrendPoints); // This is now calculated by useMemo
            // setTrendCategoryTitles(scrapingDates); // This is now calculated by useMemo
            // setTrendData(trendData); // This is now calculated by useMemo
            // setTrendDataV(trendDataV); // This is now calculated by useMemo
            setColorSeries(serieColors);
            setColorVSeries(serieVColors);
        }
    }, [trendResult, serieColors, serieVColors]);

    const minMaxValues = useMemo(() => {
        return Object.values(trendData).reduce(
            (acc, nextElem) => {
                const elemMaxValue = nextElem.reduce((acc, nextElem) => {
                    const serieMaxValue = nextElem.data.reduce((acc, nextElem) => {
                        return acc > nextElem[1] ? acc : nextElem[1];
                    }, 0);
                    return acc > serieMaxValue ? acc : serieMaxValue;
                }, 0);
                const elemMinValue = nextElem.reduce((acc, nextElem) => {
                    const serieMinValue = nextElem.data
                        .filter((item) => item !== null && item[1] !== null)
                        .reduce((acc, nextElem) => {
                            return acc < nextElem[1] ? acc : nextElem[1];
                        }, Infinity);
                    return acc < serieMinValue ? acc : serieMinValue;
                }, Infinity);
                return {
                    maxValue: acc.maxValue > elemMaxValue ? acc.maxValue : elemMaxValue,
                    minValue: acc.minValue < elemMinValue ? acc.minValue : elemMinValue,
                };
            },
            { maxValue: 0, minValue: Infinity },
        );
    }, [trendData]);

    useEffect(() => {
        const dataSerie = referenceResult?.data().series().firstForMeasure(seriesBy[selectedKpi.index]);
        if (dataSerie) {
            const referenceValue = dataSerie.dataPoints().map((dp) => (selectedKpi.index === 24 ? Number((Number(dp.rawValue) * 100).toFixed(2)) : Math.round(Number(dp.rawValue))))[0];
            setBasketReference((prev) => ({
                ...prev,
                value: referenceValue,
            }));
        }
    }, [referenceResult, selectedKpi]);

    useEffect(() => {
        const { Basket_BarChart, Basket_Trends, Basket_Overview } = userPreferenceContext?.user?.basket_sections || {};

        if (printStatus) {
            setShowBarFigures(true);
            setShowTrendCharts(true);
            setShowGraph(true);
        } else {
            setShowBarFigures(Basket_BarChart);
            setShowTrendCharts(Basket_Trends);
            setShowGraph(Basket_Overview);
        }
    }, [printStatus, userPreferenceContext]);

    const { basketSlotsPages, basketTSlotsPages } = useMemo(() => {
        const valableSlots = custSlots.filter((elem) => monthData.find((item) => item.custId === elem));
        const basketSlotsPages = [];
        for (let i = 0; i < valableSlots?.length; i += 4) {
            basketSlotsPages.push(valableSlots.slice(i, i + 4));
        }
        const basketTSlotsPages = [];
        for (let i = 0; i < valableSlots?.length; i += 2) {
            basketTSlotsPages.push(valableSlots.slice(i, i + 2));
        }
        return { valableSlots, basketSlotsPages, basketTSlotsPages };
    }, [custSlots, monthData]);

    const { basketVSlotsPages, basketTVSlotsPages, missingVehicleSlots } = useMemo(() => {
        const valableVSlots = versionSlots.filter((elem) => monthVData.find((item) => item.versionStd === elem));
        const basketVSlotsPages = [];
        for (let i = 0; i < valableVSlots?.length; i += 4) {
            basketVSlotsPages.push(valableVSlots.slice(i, i + 4));
        }
        const basketTVSlotsPages = [];
        for (let i = 0; i < valableVSlots?.length; i += 2) {
            basketTVSlotsPages.push(valableVSlots.slice(i, i + 2));
        }
        const missingVehicleSlots = versionSlots.filter((elem) => !valableVSlots.includes(elem));
        return { valableVSlots, basketVSlotsPages, basketTVSlotsPages, missingVehicleSlots };
    }, [versionSlots, monthVData]);

    const onSelectBasketReference = useCallback(
        (refObj) => {
            const { customer, versionStd } = refObj;
            if (basket.type === "user") editBasket(basket.id, { reference_line: { customer, versionStd } });
            setBasketReference({ ...refObj });
        },
        [basket, editBasket],
    );

    const getVehicleAvailability = useCallback(
        (versionStd) => {
            if (!resultAllVehicles?.data()) return [];

            const availabilityData = resultAllVehicles
                ?.data()
                ?.slices()
                ?.toArray()
                .map((slice) => slice.sliceTitles())
                .filter(([custId, version, duration]) => version === versionStd)
                .reduce((acc, [custId]) => {
                    // Only keep unique customers
                    if (!acc.includes(getCustlabelByCustId(custId))) {
                        acc.push(getCustlabelByCustId(custId));
                    }
                    return acc;
                }, []);

            return availabilityData;
        },
        [resultAllVehicles, getCustlabelByCustId],
    );

    const versionStdMap = useMemo(() => {
        return basket.basketFilters
            .filter((elem) => elem.type === "version_std")
            .reduce((acc, vehicle) => {
                acc[vehicle.value] = vehicle;
                return acc;
            }, {});
    }, [basket.basketFilters]);

    const handleSelectVehicle = useCallback((vehicle) => {
        setSelectedVehicle(vehicle);
        setShowVersionMatch(true);
    }, []);

    const showInBar = printMode !== "trends" && printMode !== "summary" && printMode !== "lfgraph";
    const showInTrends = printMode !== "bars" && printMode !== "Overview" && printMode !== "summary" && printMode !== "lfgraph";

    // Calculate player version availability data
    const playerVersionAvailability = useMemo(() => {
        if (!resultAllVehicles?.data()) return {};

        const availabilityData = {};

        const slices = resultAllVehicles?.data()?.slices()?.toArray() || [];

        // Process slice data to build the player-to-versions mapping
        slices.forEach((slice) => {
            const [custId, version] = slice.sliceTitles();

            // Only include versions that are in our basket
            if (versionSlots.includes(version)) {
                if (!availabilityData[custId]) {
                    availabilityData[custId] = new Set();
                }
                availabilityData[custId].add(version);
            }
        });

        // Convert sets to counts for easier consumption in the component
        return Object.entries(availabilityData).reduce((acc, [custId, versions]) => {
            acc[custId] = {
                count: versions.size,
                versions: Array.from(versions),
            };
            return acc;
        }, {});
    }, [resultAllVehicles, versionSlots]);

    return (
        <div className={!printStatus ? "w-full flex flex-col items-stretch gap-4" : ""}>
            {showInBar && (
                <div className="break-after-page">
                    {!printStatus && (
                        <>
                            <div className="pt-2">
                                <span className="grow leading-5 text-xl font-bold text-indigo-700">Basket {basket.name}</span>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 justify-between">
                                    <Selectedcust playerVersionAvailability={playerVersionAvailability} />
                                    {
                                        <div className="flex items-center gap-2 relative mb-24">
                                            <DndButton setIsOpen={() => setIsOpen(true)} />
                                            {/* @ts-ignore */}
                                            <BasketReference reference={basketReference} custIds={custIds} vehiclesStd={versionSlots} onSelect={onSelectBasketReference} />
                                            {basketReference?.customer && basketReference?.versionStd && (
                                                <button
                                                    className="bg-indigo-700 hover:bg-indigo-500 text-white font-semibold shadow-button py-1.5 px-2 rounded-md flex items-center justify-center"
                                                    title="Remove reference line"
                                                    onClick={() => {
                                                        if (basket.type === "user") editBasket(basket.id, { reference_line: { customer: undefined, versionStd: undefined } });
                                                        setBasketReference({ customer: undefined, versionStd: undefined, value: undefined });
                                                    }}
                                                >
                                                    <FaTrash className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    }
                                </div>
                                {!printStatus && missingVehicleSlots.length > 0 && (
                                    <div className="flex flex-row gap-4 items-start text-xs">
                                        <span className="font-medium text-indigo-700 text-sm mb-2 w-fit whitespace-nowrap">Missing vehicles:</span>
                                        <div className="grid grid-cols-3 items-center gap-2 pb-2">
                                            {missingVehicleSlots.map((item, index) => {
                                                const vehicle = versionStdMap[item];
                                                return <MissingVehicle key={index} vehicle={vehicle} getVehicleAvailability={getVehicleAvailability} onSelect={handleSelectVehicle} />;
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    <div className="pt-4" ref={ref}>
                        {!printStatus && (
                            <div className="p-3 rounded-md flex justify-between items-stretch cursor-pointer hover:bg-gray-200" onClick={() => setShowGraph(!showGraph)}>
                                <span className="font-semibold text-lg text-steel">Overview</span>
                                <UpDownButton visibilityState={showGraph} />
                            </div>
                        )}
                        {showGraph && printMode !== "bars" && (
                            <div className={printStatus ? "break-after-page" : ""}>
                                {printStatus && (
                                    <PdfHeader adjdeposititle={adjdeposititle} country={countryLabel} channel1={filterChannel1.positiveAttributeFilter.in?.values[0]} duration={filterDuration.positiveAttributeFilter.in?.values[0]} kpi={selectedKpi.name} basketName={basket.name} date={monthDate} />
                                )}
                                <div
                                    style={{
                                        display: printStatus ? "flex" : "block",
                                        justifyContent: printStatus ? "center" : "initial",
                                        alignItems: printStatus ? "center" : "initial",
                                        height: printStatus ? (printMode === "Overview" ? "600px" : "800px") : "auto",
                                    }}
                                >
                                    <div className={`${printStatus ? "mt-4 border-2 border-gray-300 rounded" : ""} min-w-full`}>
                                        <BasketSynthesisChart
                                            kpi={selectedKpi}
                                            printStatus={printStatus}
                                            data={monthVData}
                                            listPrices={listPrices}
                                            filters={[versionStdFilter, filterChannel1, filterCountryId, filterDuration, custIdFilters, filterMthCode, filterExtrapType, filterRolledForward]}
                                            basketReference={basketReference}
                                            isLoading={status === "loading"}
                                            rolledForwardDataPoints={rolledForwardDataPoints}
                                            title={
                                                printStatus
                                                    ? "Overview"
                                                    : `${basket.name} Monthly rate by player | ${countryLabel} | ${filterChannel1.positiveAttributeFilter.in?.values[0]} | ${filterDuration.positiveAttributeFilter.in?.values[0]} ${adjdeposititle ? " | " + adjdeposititle : ""} | ${selectedKpi.name}`
                                            }
                                            kpiSettings={userPreferenceContext.kpiSettings}
                                            menuButtonPosition="inside"
                                            width={printStatus ? (printMode === "Overview" ? 1024 : 1200) : overviewDivWidth}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {!printStatus && (
                        <>
                            <div className="p-3 mt-4 rounded-md flex justify-between items-stretch cursor-pointer hover:bg-gray-200" onClick={() => setShowBarFigures(!showBarFigures)}>
                                <span className="font-semibold text-lg text-steel">Monthly rate by {displayMode.title}</span>
                                <UpDownButton visibilityState={showBarFigures} />
                            </div>
                        </>
                    )}
                    {showBarFigures &&
                        printMode !== "Overview" &&
                        (status === "loading" ? (
                            <LoadingComponent />
                        ) : displayMode.key === "player" ? (
                            basketSlotsPages.map((custSlots, index) => (
                                <div key={`cust-page-${index}`} className={"break-after-page " + (basketSlotsPages?.length !== 1 ? "mt-1" : "")}>
                                    {printStatus && (
                                        <PdfHeader
                                            adjdeposititle={adjdeposititle}
                                            country={countryLabel}
                                            channel1={filterChannel1.positiveAttributeFilter.in?.values[0]}
                                            duration={filterDuration.positiveAttributeFilter.in?.values[0]}
                                            kpi={selectedKpi.name}
                                            basketName={basket.name}
                                            date={monthDate}
                                        />
                                    )}
                                    {
                                        <div className={printStatus ? "grid py-4 gap-4 grid-cols-basketPdfGrid" : "grid grid-cols-2 py-4 gap-4"}>
                                            {custSlots.map((custIndex, index) => {
                                                let player = monthData?.find((elem) => elem.custId === custIndex);
                                                let playerkeys = player && Object.keys(player).filter((key) => key !== "custId");
                                                let barChartData = playerkeys
                                                    ?.map((key) => {
                                                        return {
                                                            title: key,
                                                            dataPoint: player[key],
                                                        };
                                                    })
                                                    .filter((elem) => elem.dataPoint !== null);
                                                barChartData?.sort((a, b) => b.dataPoint - a.dataPoint);
                                                let filterCustId = newPositiveAttributeFilter(Md.CustId, [custIndex]);
                                                return (
                                                    <BasketBarChart
                                                        rolledForwardDataPoints={rolledForwardDataPoints}
                                                        kpiSettings={userPreferenceContext.kpiSettings}
                                                        displayMode={displayMode}
                                                        filters={[versionStdFilter, filterChannel1, filterCountryId, filterDuration, filterCustId, filterMthCode]}
                                                        key={`${custIndex}-bar`}
                                                        custIndex={custIndex}
                                                        title={
                                                            printStatus
                                                                ? getCustlabelByCustId(custIndex)
                                                                : country === "GB"
                                                                ? `${getCustlabelByCustId(custIndex)} - ${countryLabel} - ${filterChannel1.positiveAttributeFilter.in?.values[0]} - ${filterDuration?.positiveAttributeFilter?.in?.values[0]}
                                                                ${adjdeposititle ? adjdeposititle + "-" : ""} ${selectedKpi.name}`
                                                                : `${getCustlabelByCustId(custIndex)} - ${countryLabel} - ${filterChannel1.positiveAttributeFilter.in?.values[0]} - ${filterDuration?.positiveAttributeFilter?.in?.values[0]} - ${selectedKpi.name}`
                                                        }
                                                        subtitle={!printStatus ? basket.name : null}
                                                        // chartData={props.basket.loadVizData("bar", custIndex)}
                                                        // onComplete={(data) => {
                                                        //     props.basket.storeVizData("bar", custIndex, data);
                                                        // }}
                                                        reference={basketReference}
                                                        data={barChartData}
                                                        chartProps={{
                                                            barWidth: printStatus ? 20 : 25,
                                                            width: printStatus ? 580 : null,
                                                            height: printStatus ? 350 : 450,
                                                            pdfMode: printStatus,
                                                            maxValue: scale.maxValue,
                                                            marginLeft: barChartData?.length > 0 ? 230 : null,
                                                            screenshotName: `basket-${basket.name}-spot-figure-${custIndex}`,
                                                            menuButtonPosition: "inside",
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    }
                                    {printStatus && basketReference?.customer && basketReference?.versionStd && (
                                        <div className="mt-2">
                                            <span className="font-semibold text-sm text-steel">
                                                Reference: {getCustlabelByCustId(basketReference.customer)} - {basketReference.versionStd}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            basketVSlotsPages.map((versionSlots, index) => (
                                <div key={`version-page-${index}`} className={"break-after-page " + (basketSlotsPages?.length !== 1 ? "mt-1" : "")}>
                                    {printStatus && (
                                        <PdfHeader
                                            adjdeposititle={adjdeposititle}
                                            country={countryLabel}
                                            channel1={filterChannel1.positiveAttributeFilter.in?.values[0]}
                                            duration={filterDuration.positiveAttributeFilter.in?.values[0]}
                                            kpi={selectedKpi.name}
                                            basketName={basket.name}
                                            date={monthDate}
                                        />
                                    )}
                                    {
                                        <div className={printStatus ? "grid py-4 gap-4 grid-cols-basketPdfGrid" : "grid grid-cols-2 py-4 gap-4"}>
                                            {versionSlots.map((version_std, index) => {
                                                let version = monthVData?.find((elem) => elem.versionStd === version_std);
                                                let versionkeys = version && Object.keys(version).filter((key) => key !== "versionStd");
                                                let barChartData = versionkeys
                                                    ?.map((key) => {
                                                        return {
                                                            title: key,
                                                            dataPoint: version[key],
                                                        };
                                                    })
                                                    .filter((elem) => elem.dataPoint !== null);
                                                barChartData?.sort((a, b) => b.dataPoint - a.dataPoint);
                                                let filterVersion = newPositiveAttributeFilter(Md.VersionStd, [version_std]);
                                                return (
                                                    <BasketBarChart
                                                        rolledForwardDataPoints={rolledForwardDataPoints}
                                                        kpiSettings={userPreferenceContext.kpiSettings}
                                                        filters={[filterVersion, filterChannel1, filterCountryId, filterDuration, custIdFilters, filterMthCode]}
                                                        displayMode={displayMode}
                                                        key={`${version_std}-bar`}
                                                        custIndex={version_std}
                                                        title={
                                                            printStatus
                                                                ? version_std
                                                                : `${version_std}
                                            - ${countryLabel}
                                            - ${filterChannel1.positiveAttributeFilter.in?.values[0]} - ${filterDuration?.positiveAttributeFilter?.in?.values[0]} - ${adjdeposititle ? adjdeposititle + "-" : ""} ${selectedKpi.name}`
                                                        }
                                                        subtitle={!printStatus ? basket.name : null}
                                                        // chartData={props.basket.loadVizData("bar", custIndex)}
                                                        // onComplete={(data) => {
                                                        //     props.basket.storeVizData("bar", custIndex, data);
                                                        // }}
                                                        reference={basketReference}
                                                        data={barChartData}
                                                        chartProps={{
                                                            barWidth: printStatus ? 20 : 25,
                                                            width: printStatus ? 580 : null,
                                                            height: printStatus ? 350 : 450,
                                                            pdfMode: printStatus,
                                                            maxValue: scale.maxValue,
                                                            marginLeft: barChartData?.length > 0 ? 150 : null,
                                                            screenshotName: `basket-${basket.name}-spot-figure-${version_std}`,
                                                            menuButtonPosition: "inside",
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    }
                                    {printStatus && basketReference?.customer && basketReference?.versionStd && (
                                        <div className="mt-2">
                                            <span className="font-semibold text-sm text-steel">
                                                Reference: {getCustlabelByCustId(basketReference.customer)} - {basketReference.versionStd}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))
                        ))}
                </div>
            )}
            {showInTrends && (
                <div className="break-after-page">
                    {!printStatus && (
                        <div className="p-3 rounded-md flex justify-between items-stretch cursor-pointer hover:bg-gray-200" onClick={() => setShowTrendCharts(!showTrendCharts)}>
                            <span className="font-semibold text-lg text-steel">Monthly rate trends by {displayMode.title}</span>
                            <UpDownButton visibilityState={showTrendCharts} />
                        </div>
                    )}
                    {showTrendCharts &&
                        (referenceStatus === "loading" ? (
                            <div className="my-2">
                                <LoadingComponent />
                            </div>
                        ) : displayMode.key === "player" ? (
                            basketTSlotsPages.map((custSlots, index) => (
                                <div key={`cust-page-${index}`} className={"break-after-page " + (basketTSlotsPages?.length !== 1 ? "mt-1" : "")}>
                                    {printStatus && (
                                        <PdfHeader
                                            adjdeposititle={adjdeposititle}
                                            country={countryLabel}
                                            channel1={filterChannel1.positiveAttributeFilter.in?.values[0]}
                                            duration={filterDuration.positiveAttributeFilter.in?.values[0]}
                                            kpi={selectedKpi.name}
                                            basketName={basket.name}
                                            date={monthDate}
                                        />
                                    )}
                                    {
                                        <div className={printStatus ? "grid py-4 gap-4" : "flex flex-col gap-4 items-stretch"}>
                                            {custSlots.map((custIndex, index) => {
                                                let filterCustId = newPositiveAttributeFilter(Md.CustId, [custIndex]);

                                                return (
                                                    <BasketLineChart
                                                        rolledForwardTrendPoints={rolledForwardTrendPoints}
                                                        kpiSettings={userPreferenceContext.kpiSettings}
                                                        // @ts-ignore
                                                        filters={basketTrendFilters.map((obj) => (obj?.positiveAttributeFilter?.displayForm.identifier === Md.CustId.attribute.displayForm.identifier ? filterCustId : obj))}
                                                        displayMode={displayMode}
                                                        custIndex={custIndex}
                                                        minValue={minMaxValues?.minValue * 0.9 || undefined}
                                                        maxValue={minMaxValues?.maxValue * 1.1 || undefined}
                                                        key={`${custIndex}-line`}
                                                        data={trendData[custIndex]}
                                                        categories={scrapingDates}
                                                        title={
                                                            printStatus
                                                                ? getCustlabelByCustId(custIndex)
                                                                : `${getCustlabelByCustId(custIndex)}
                                                                - ${countryLabel}
                                                                - ${filterChannel1.positiveAttributeFilter.in?.values[0]} - ${filterDuration.positiveAttributeFilter.in?.values[0]} - ${adjdeposititle ? adjdeposititle + "-" : ""} ${selectedKpi.name}`
                                                        }
                                                        subtitle={!printStatus ? basket.name : null}
                                                        height={printStatus ? 300 : 530}
                                                        width={printStatus ? 1200 : null}
                                                        chartProps={{
                                                            pdfMode: printStatus,
                                                            screenshotName: `basket-${basket.name}-trends-${custIndex}`,
                                                            enableLegend: !printStatus,
                                                            marginBottom: printStatus ? 70 : trendData[custIndex]?.length > 6 ? 180 : 120,
                                                            menuButtonPosition: "inside",
                                                            scaleUnit: defaultUserPref.fl_weekly ? "datetime" : "default",
                                                            // maxValue: maxTrendsValue * 1.1,
                                                            // minValue: minTrendsValue * 0.8,
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    }
                                    {printStatus && (
                                        <div className="legend h-min w-full grid grid-cols-2 gap-1">
                                            {Object.entries(colorSeries).map(([name, color]) => (
                                                <div key={name} className="flex gap-2 items-center">
                                                    <div
                                                        style={{
                                                            width: "15px",
                                                            // height: "10px",
                                                            borderBottom: `solid ${color}`,
                                                        }}
                                                    ></div>
                                                    <span className="text-xs font-normal">{name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            basketTVSlotsPages.map((versionSlots, index) => {
                                return (
                                    <div key={`version-page-${index}`} className={"break-after-page " + (basketTVSlotsPages?.length !== 1 ? "mt-1" : "")}>
                                        {printStatus && (
                                            <PdfHeader
                                                adjdeposititle={adjdeposititle}
                                                country={countryLabel}
                                                channel1={filterChannel1.positiveAttributeFilter.in?.values[0]}
                                                duration={filterDuration.positiveAttributeFilter.in?.values[0]}
                                                kpi={selectedKpi.name}
                                                basketName={basket.name}
                                                date={monthDate}
                                            />
                                        )}
                                        <div className={printStatus ? "grid py-4 gap-4" : "flex flex-col gap-4 items-stretch"}>
                                            {versionSlots.map((version, index) => {
                                                let filterVersion = newPositiveAttributeFilter(Md.VersionStd, [version]);
                                                return (
                                                    <BasketLineChart
                                                        rolledForwardTrendPoints={rolledForwardTrendPoints}
                                                        kpiSettings={userPreferenceContext.kpiSettings}
                                                        minValue={minMaxValues?.minValue * 0.9 || undefined}
                                                        maxValue={minMaxValues?.maxValue * 1.1 || undefined}
                                                        //@ts-ignore
                                                        filters={basketTrendFilters.map((obj) => (obj?.positiveAttributeFilter?.displayForm.identifier === Md.VersionStd.attribute.displayForm.identifier ? filterVersion : obj))}
                                                        displayMode={displayMode}
                                                        custIndex={version}
                                                        key={`${version}-line`}
                                                        data={trendDataV[version]}
                                                        categories={scrapingDates}
                                                        title={
                                                            printStatus
                                                                ? version
                                                                : `${version}
                                                                    - ${countryLabel}
                                                                    - ${filterChannel1.positiveAttributeFilter.in?.values[0]} - ${filterDuration.positiveAttributeFilter.in?.values[0]} - ${adjdeposititle ? adjdeposititle + "-" : ""} ${selectedKpi.name}`
                                                        }
                                                        subtitle={!printStatus ? basket.name : null}
                                                        height={printStatus ? 300 : 520}
                                                        width={printStatus ? 1200 : null}
                                                        chartProps={{
                                                            pdfMode: printStatus,
                                                            screenshotName: `basket-${basket.name}-trends-${version}`,
                                                            enableLegend: !printStatus,
                                                            marginBottom: printStatus ? 70 : 120,
                                                            menuButtonPosition: "inside",
                                                            scaleUnit: defaultUserPref.fl_weekly ? "datetime" : "default",
                                                            // maxValue: maxTrendsValue * 1.1,
                                                            // minValue: minTrendsValue * 0.8,
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                        {printStatus && (
                                            <div className="legend h-min w-full grid grid-cols-2 gap-1">
                                                {Object.entries(colorVSeries).map(([name, color], idx) => (
                                                    <div key={`legend-${idx}`} className="flex gap-2 items-center">
                                                        <div
                                                            style={{
                                                                width: "15px",
                                                                // height: "10px",
                                                                borderBottom: `solid ${getColorByCustId(name)}`,
                                                            }}
                                                        ></div>
                                                        <span className="text-xs font-normal">{getCustlabelByCustId(name)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ))}
                </div>
            )}
            {showVersionMatch && selectedVehicle && (
                <BasketVersionMatch
                    basket={basket}
                    Vehicle={selectedVehicle}
                    onCancel={() => setShowVersionMatch(false)}
                    seriesBy={[...seriesBy, Md.Emissions.Avg, Md.Doors.Avg]}
                    slicesBy={[Md.VersionStd, Md.CustId, Md.CorrModel, Md.VehSegment, Md.BodyStyle, Md.GearsPlus, Md.Fuel, Md.CorrMake, Md.VehType]}
                    filters={[filterCountryId, filterChannel1, filterDuration, filterMthCode]}
                    custSlots={custSlots}
                    kpi={selectedKpi}
                />
            )}
            <DnDModal isOpen={isOpen} setIsOpen={setIsOpen} />
        </div>
    );
}
export { BasketViz, PdfHeader };
